import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Dialog } from '@/components-new/Dialog/Dialog';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { TextArea } from '@/components/form/fields/TextArea/TextArea';
import { Notification } from '@/components/Notification/Notification';
import { DATE_TIME_FORMAT, formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { InvoiceLinkType } from '@/types/invoiceVaultTypes';
import { runInvoiceLinkAction } from '../../../api/api';
import { KeyValueList } from '../../../styles';
import { DialogButtons } from '@/components-new/Dialog/styles';

type PropsType = { link: InvoiceLinkType; canResolve: boolean; onClose: () => void; onResolved: () => void };

export const DisputeModal = ({ link, canResolve, onClose, onResolved }: PropsType) => {
  const [resolution, setResolution] = useState('');
  const [error, setError] = useState('');
  const dispute = link.open_dispute;

  const resolve = useMutation({
    mutationFn: () => runInvoiceLinkAction(link.id, 'resolve-dispute', { resolution: resolution.trim() }),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      onResolved();
    },
  });

  return (
    <Dialog header={`Dispute on ${link.invoice_number}`} isOpen onClose={onClose} width='560px'>
      {dispute ? (
        <KeyValueList>
          <dt>Raised by</dt>
          <dd>{dispute.raised_by_label || 'The payer'}</dd>
          <dt>When</dt>
          <dd>{formatDate(dispute.date_created, DATE_TIME_FORMAT)}</dd>
          <dt>Reason</dt>
          <dd>{dispute.reason_label}</dd>
          <dt>Details</dt>
          <dd>{dispute.details || 'No details given.'}</dd>
        </KeyValueList>
      ) : (
        <Typography variant='bodySmall'>No open dispute on this invoice.</Typography>
      )}

      {dispute && canResolve && (
        <Box display='flex' flexDirection='column' gap='8px' marginTop='16px'>
          <Typography variant='bodySmall' fontWeight='medium'>
            Reply and reopen the instructions
          </Typography>
          <TextArea
            name='resolution'
            placeholder='Rush fee was on the signed PO, see attached. Resend the corrected invoice if needed.'
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            rows={4}
          />
          <Typography variant='caption'>
            The payer sees your reply the next time they open the link, and the instructions unlock again.
          </Typography>
        </Box>
      )}
      {error && <Notification variant='error'>{error}</Notification>}

      <DialogButtons>
        <ButtonNew variant='secondary' size='medium' onClick={onClose}>
          Close
        </ButtonNew>
        {dispute && canResolve && (
          <ButtonNew
            size='medium'
            disabled={resolution.trim().length < 3}
            isLoading={resolve.isPending}
            onClick={() => resolve.mutate()}
          >
            Resolve dispute
          </ButtonNew>
        )}
      </DialogButtons>
    </Dialog>
  );
};
